import { supabase } from "@/libs/supabase";
import type { Goal, DbGoal } from "@/types/goal";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

// データベースの型からアプリケーションの型への変換関数
export const convertToAppGoal = (dbGoal: DbGoal): Goal => ({
	id: dbGoal.id,
	userId: dbGoal.user_id,
	title: dbGoal.title,
	description: dbGoal.description || undefined,
	category: dbGoal.category,
	startDate: dbGoal.start_date,
	endDate: dbGoal.end_date,
	priority: dbGoal.priority,
	status: dbGoal.status,
	createdAt: dbGoal.created_at,
	updatedAt: dbGoal.updated_at,
});

export const goalService = {
	// 目標一覧の取得
	async getGoals() {
		const { data, error } = await supabase
			.from("goals")
			.select("*")
			.order("created_at", { ascending: false });

		if (error) throw error;
		return (data as unknown as DbGoal[]).map(convertToAppGoal);
	},

	// 特定の目標の取得
	async getGoalById(id: string) {
		const { data, error } = await supabase
			.from("goals")
			.select("*")
			.eq("id", id)
			.single();

		if (error) throw error;
		return convertToAppGoal(data as unknown as DbGoal);
	},

	// 目標の作成
	async createGoal(
		goal: Omit<Goal, "id" | "userId" | "createdAt" | "updatedAt">,
	) {
		const { data, error } = await supabase
			.from("goals")
			.insert([
				{
					title: goal.title,
					description: goal.description,
					category: goal.category,
					start_date: goal.startDate,
					end_date: goal.endDate,
					priority: goal.priority,
					status: goal.status,
					user_id: (await supabase.auth.getUser()).data.user?.id,
				},
			])
			.select()
			.single();

		if (error) throw error;
		return convertToAppGoal(data as unknown as DbGoal);
	},

	// 目標の更新
	async updateGoal(
		id: string,
		goal: Partial<Omit<Goal, "id" | "userId" | "createdAt" | "updatedAt">>,
	) {
		const updateData: Partial<DbGoal> = {
			title: goal.title,
			description: goal.description,
			category: goal.category,
			start_date: goal.startDate,
			end_date: goal.endDate,
			priority: goal.priority,
			status: goal.status,
		};

		const { data, error } = await supabase
			.from("goals")
			.update(updateData)
			.eq("id", id)
			.select()
			.single();

		if (error) throw error;
		return convertToAppGoal(data as unknown as DbGoal);
	},

	// 目標の削除
	async deleteGoal(id: string) {
		const { error } = await supabase.from("goals").delete().eq("id", id);
		if (error) throw error;
	},

	// リアルタイム更新のサブスクリプション
	subscribeToChanges(
		callback: (payload: RealtimePostgresChangesPayload<DbGoal>) => void,
	) {
		return supabase
			.channel("goal_changes")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "goals",
				},
				callback,
			)
			.subscribe();
	},
};
